<script lang="ts">
	import Button from "@components/common/Button.svelte";
	import { ButtonSize } from "@components/common/ButtonSize.js";
	import { ButtonVariant } from "@components/common/ButtonVariant.js";
	import Formatted from "@components/common/Formatted.svelte";
	import Heading from "@components/common/Heading.svelte";
	import Icon from "@components/common/Icon.svelte";
	import Modal from "@components/common/Modal.svelte";
	import Form from "@components/form/Form.svelte";
	import Input from "@components/form/Input.svelte";
	import ClientZoneLayout from "@components/layout/ClientZoneLayout.svelte";
	import { Auth } from "@utils/Auth.svelte.js";
	import { getTranslate } from "@utils/getTranslate.js";
	import padlockIcon from "@core/assets/icons/padlock.svg?raw";
	import { CognitoProvider } from "@core/schema/CognitoProvider.js";
	import type { Customer } from "@core/schema/Customer.js";
	import { createReactiveBreakpoint } from "@core/utils/createReactiveBreakpoint.js";
	import { createPromiseWithStatus } from "@core/utils/svelte/createPromiseWithStatus.svelte.js";
	import { formatPhoneNumberOnInput } from "@core/utils/svelte/formatPhoneNumberOnInput.js";
	import translations from "./AccountDetails.translations.json" with { type: "json" };

	const translate = getTranslate(translations);

	const saveChanges = createPromiseWithStatus(async (form: HTMLFormElement): Promise<void> => {
		const formData = new FormData(form);
		const invoiceEmail = formData.get("invoiceEmail");

		await Auth.editMe({
			fullName: formData.get("fullName") as string,
			phoneNumber: formData.get("phone") as string,
			invoiceEmail: typeof invoiceEmail === "string" ? invoiceEmail || undefined : undefined,
		});
	});

	let showForgotPasswordModal = $state(false);

	const isDesktop = createReactiveBreakpoint(">lg");
	const changePassword = createPromiseWithStatus(Auth.changePassword.bind(Auth));
</script>

<ClientZoneLayout>
	{#snippet children(customer: Customer)}
		<section class="max-w-164 w-full mx-auto min-h-[calc(100vh-12rem)]">
			<Heading
				level={1}
				class="font-antipol text-1.5xl/tighter lg:text-2xl/tighter lg:mb-8 mb-4"
				text={translate("title")}
			/>
			<Form
				class="flex flex-col lg:gap-4 gap-2"
				onsubmit={(_, form: HTMLFormElement): void => void saveChanges.call(form)}
			>
				<Input
					class="bg-white"
					required
					name="fullName"
					autocomplete="name"
					placeholder={translate("fullName")}
					defaultValue={customer.fullName}
				/>
				<Input
					class="bg-white"
					required
					name="phone"
					oninput={formatPhoneNumberOnInput}
					autocomplete="tel"
					placeholder={translate("phone")}
					defaultValue={customer.phoneNumber}
				/>
				<Input
					class="bg-white"
					name="invoiceEmail"
					autocomplete="off"
					placeholder={translate("invoiceEmail")}
					defaultValue={customer.invoiceEmail}
				/>
				<Input
					class="bg-white"
					required
					name="email"
					readonly
					disabled
					placeholder={translate("email")}
					value={customer.email}
				/>
				<p class="text-sm text-fcrdarkgray px-4">
					<Formatted text={translate("noEmailChangeYet")} />
				</p>
				{#if customer.cognitoProvider !== CognitoProvider.Google}
					<button
						type="button"
						class="flex w-full px-6 items-center gap-2 my-2 lg:mt-6 lg:mb-4 h-16 border-y border-fcrlightgray-300"
						onclick={(): void => {
							showForgotPasswordModal = true;
						}}
					>
						<Icon icon={padlockIcon} class="size-6 text-fcrgray" />
						<span class="font-medium text-base text-fcrdarkgray">
							<Formatted text={translate("changePassword")} />
						</span>
					</button>
				{/if}
				<Button
					type="submit"
					variant={ButtonVariant.Dark}
					size={isDesktop.current ? ButtonSize.Large : ButtonSize.Medium}
				>
					<Formatted text={translate("saveChanges")} />
				</Button>

				<p class="text-base my-4 h-6">
					{#if saveChanges.value.status === "success"}
						<span class="text-fcrdarkgray">
							<Formatted text={translate("updateSuccess")} />
						</span>
					{:else if saveChanges.value.status === "error"}
						{@const { error } = saveChanges.value}
						<span class="text-fcrerror">
							<Formatted
								text={translate("updateError", {
									error: error instanceof Error ? error.message : String(error),
								})}
							/>
						</span>
					{/if}
				</p>
			</Form>
		</section>
		{#if customer.cognitoProvider !== CognitoProvider.Google}
			<Modal
				bind:show={showForgotPasswordModal}
				onclose={(): void => {
					changePassword.value.status = "initial";
				}}
				heading={translate("forgotPasswordConfirmation")}
			>
				<Form
					class="flex flex-col gap-4 mt-4"
					onsubmit={async (_event, form: HTMLFormElement): Promise<void> => {
						const newPassword = form.elements.namedItem("newPassword") as HTMLInputElement;
						const newPasswordAgain = form.elements.namedItem("newPasswordAgain") as HTMLInputElement;
						const currentPassword = form.elements.namedItem("currentPassword") as HTMLInputElement;

						if (newPassword.value !== newPasswordAgain.value) {
							newPassword.setCustomValidity(translate("passwordMismatch"));
							newPasswordAgain.setCustomValidity(translate("passwordMismatch"));
							form.reportValidity();
							return;
						}

						await changePassword.call(currentPassword.value, newPassword.value);
					}}
				>
					<Input
						placeholder={translate("currentPassword")}
						type="password"
						name="currentPassword"
						autocomplete="current-password"
					/>
					<Input
						placeholder={translate("newPassword")}
						type="password"
						name="newPassword"
						autocomplete="new-password"
					/>
					<Input
						placeholder={translate("newPasswordAgain")}
						type="password"
						name="newPasswordAgain"
						autocomplete="new-password"
					/>
					<div class="flex justify-end gap-4 mt-8 sm:min-w-128">
						<Button
							variant={ButtonVariant.Outlined}
							size={ButtonSize.Medium}
							onclick={(): void => {
								showForgotPasswordModal = false;
							}}
						>
							{translate("cancel")}
						</Button>
						<Button
							variant={ButtonVariant.Dark}
							size={ButtonSize.Medium}
							loading={changePassword.value.status === "loading"}
						>
							{translate("send")}
						</Button>
					</div>
				</Form>
				{#if changePassword.value.status === "error"}
					<div class="mt-4 text-fcrerror max-w-100 mx-auto text-center">
						<Formatted text={translate("forgotPasswordError", { error: String(changePassword.value.error) })} />
					</div>
				{:else if changePassword.value.status === "success"}
					<div class="mt-4 text-fcrsuccess max-w-100 mx-auto text-center">
						<Formatted text={translate("forgotPasswordSuccess")} />
					</div>
				{/if}
			</Modal>
		{/if}
	{/snippet}
</ClientZoneLayout>
